import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase.config';
import { selectAdminError, selectAdminStatus, selectAllCustomers, fetchCustomers, getOpsOverview, clearAdminError } from '../redux/adminSlice';
import {
  fetchSupportTickets,
  setTicketStatus,
  selectSupportTickets,
  selectSupportStatus,
  selectSupportError,
  selectSupportFetched,
} from '../redux/supportSlice';
import {
  fetchEarlyAccessRequests,
  setEarlyAccessStatus,
  selectEarlyAccessRequests,
  selectEarlyAccessStatus,
  selectEarlyAccessError,
  selectEarlyAccessFetched,
} from '../redux/earlyAccessSlice';
import { useCustomerUtils, CustomerTable } from './cutsomerUtils/customerUtils';
import OverviewTab from './OverviewTab';
import SettingsTab from './SettingsTab';

const TABS = ['Overview', 'Customers', 'Support', 'Settings'];
const ACTIVE_TAB_STORAGE_KEY = 'portal.activeTab';

// Reads the remembered tab from sessionStorage; unknown/unavailable values
// fall back to the default 'Overview' tab (private-mode browsers can throw
// on sessionStorage access, and an old/foreign value should never crash the
// dashboard or land on a tab that no longer exists).
const getStoredTab = () => {
  try {
    const stored = window.sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return TABS.includes(stored) ? stored : 'Overview';
  } catch (err) {
    return 'Overview';
  }
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(getStoredTab);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', ownerName: '' });
  const [ticketFilter, setTicketFilter] = useState('All');
  // Early-access status select being saved (disabled while in flight).
  const [savingRequestId, setSavingRequestId] = useState(null);
  // Ops (2026-08-31): Security card data. Deliberately local state, not
  // Redux - see the comment above getOpsOverview in adminSlice.js for why
  // (state.status full-page-blanks the dashboard on 'loading', which would
  // hide this exact card while it loads).
  const [opsOverview, setOpsOverview] = useState(null);
  const [opsOverviewStatus, setOpsOverviewStatus] = useState('idle');
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const submittingCustomerRef = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const error = useSelector(selectAdminError);
  const status = useSelector(selectAdminStatus);
  const customers = useSelector(selectAllCustomers);
  const supportTickets = useSelector(selectSupportTickets);
  const supportStatus = useSelector(selectSupportStatus);
  const supportError = useSelector(selectSupportError);
  const supportFetched = useSelector(selectSupportFetched);
  const earlyAccessRequests = useSelector(selectEarlyAccessRequests);
  const earlyAccessStatus = useSelector(selectEarlyAccessStatus);
  const earlyAccessError = useSelector(selectEarlyAccessError);
  const earlyAccessFetched = useSelector(selectEarlyAccessFetched);

  const {
    handleAddCustomer,
    handleEditCustomer,
    handleDeleteCustomer,
    handleSendPasswordReset,
  } = useCustomerUtils();

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  // Remember the active tab across a reload (round 4) - try/catch mirrors
  // getStoredTab above; sessionStorage being unavailable just means the tab
  // won't be remembered, not a crash.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    } catch (err) {
      // ignore - private mode / storage disabled
    }
  }, [activeTab]);

  // Fetch support tickets only when the Support tab is opened for the first
  // time, not on initial dashboard mount (avoids the extra reads/costs when
  // the tab is never viewed).
  useEffect(() => {
    if (activeTab === 'Support' && !supportFetched && supportStatus !== 'loading') {
      dispatch(fetchSupportTickets());
    }
  }, [activeTab, supportFetched, supportStatus, dispatch]);

  // Early access requests (2026-10-10) live on the same tab; same
  // fetch-once-per-visit rule as the tickets above.
  useEffect(() => {
    if (activeTab === 'Support' && !earlyAccessFetched && earlyAccessStatus !== 'loading') {
      dispatch(fetchEarlyAccessRequests());
    }
  }, [activeTab, earlyAccessFetched, earlyAccessStatus, dispatch]);

  // Security card: fetch once when the Overview tab is (first) active -
  // it's the default tab, so in practice this fires immediately on load.
  // opsOverviewStatus itself is the guard (idle -> loading -> succeeded/
  // failed), so this never re-fires just from switching tabs away and back.
  useEffect(() => {
    if (activeTab === 'Overview' && opsOverviewStatus === 'idle') {
      setOpsOverviewStatus('loading');
      dispatch(getOpsOverview())
        .unwrap()
        .then((data) => {
          setOpsOverview(data);
          setOpsOverviewStatus('succeeded');
        })
        .catch(() => {
          setOpsOverviewStatus('failed');
        });
    }
  }, [activeTab, opsOverviewStatus, dispatch]);

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    if (submittingCustomerRef.current) {
      return; // sync guard: a double-click can never create two customers
    }
    submittingCustomerRef.current = true;
    setIsCreatingCustomer(true);
    try {
      await handleAddCustomer({
        name: newCustomer.name.trim(),
        email: newCustomer.email.trim(),
        ownerName: newCustomer.ownerName.trim(),
      });
      setNewCustomer({ name: '', email: '', ownerName: '' });
    } catch (err) {
      // Keep the typed values so the admin can correct and resubmit.
      alert(typeof err === 'string' ? err : 'Failed to add customer. Please try again.');
    } finally {
      submittingCustomerRef.current = false;
      setIsCreatingCustomer(false);
    }
  };

  // Round 4: clear the shared error banner when the admin navigates away
  // from whatever tab produced it - otherwise a failed edit/delete leaves a
  // permanent red banner across every tab until the next fetch/edit/delete.
  const handleTabChange = (tab) => {
    dispatch(clearAdminError());
    setActiveTab(tab);
  };

  const handleToggleTicketStatus = (ticket) => {
    const nextStatus = ticket.status === 'open' ? 'closed' : 'open';
    dispatch(setTicketStatus({ ticketId: ticket.id, status: nextStatus }));
  };

  const filteredTickets = supportTickets.filter((ticket) => {
    if (ticketFilter === 'Open') return ticket.status === 'open';
    if (ticketFilter === 'Closed') return ticket.status === 'closed';
    return true;
  });

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      console.error("Error signing out: ", err);
    }
  };

  // Round 4: only blank the whole page while there is nothing to show yet.
  // Once customers have loaded once, a later refetch (editCustomer/
  // deleteCustomer/fetchCustomers all flip status to 'loading') keeps the
  // layout on screen - see the inline "Refreshing…" indicator in the header
  // below instead of hiding everything again.
  const initialLoading = status === 'loading' && customers.length === 0;
  const isRefreshing = status === 'loading' && customers.length > 0;

  if (initialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(160deg,#eef0f3,#c6cdd6)] text-[#1d2b33]">
        Loading dashboard...
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[linear-gradient(160deg,#eef0f3,#c6cdd6)]">
      <header className="sticky top-0 z-20 flex h-16 flex-none items-center justify-between gap-4 border-b border-[rgba(0,0,0,.2)] bg-[linear-gradient(120deg,#4a5c70,#374a5e_55%,#2a3a4c)] px-6 shadow-sm">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-lg font-bold text-white">
            {/* Below 900px the sidebar brand block is hidden, so the brand
                lives here instead. */}
            <span className="min-[900px]:hidden">Simplicity Dynamics · </span>
            {activeTab}
          </h1>
          {isRefreshing && (
            <span className="flex-none text-xs font-semibold text-[rgba(215,226,236,.7)]">Refreshing…</span>
          )}
        </div>
        <button
          className="flex-none rounded-lg border border-white/25 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-white/10"
          onClick={handleSignOut}
        >
          Sign Out
        </button>
      </header>

      <div className="flex flex-1 flex-col min-[900px]:flex-row">
        <nav className="flex flex-row items-center gap-1 overflow-x-auto bg-gradient-to-b from-[#4a5c70] to-[#1a2733] px-3 py-3 min-[900px]:sticky min-[900px]:top-16 min-[900px]:h-[calc(100vh-4rem)] min-[900px]:w-56 min-[900px]:flex-none min-[900px]:flex-col min-[900px]:items-stretch min-[900px]:gap-1 min-[900px]:overflow-x-hidden min-[900px]:overflow-y-auto min-[900px]:py-4">
          <div className="mb-4 hidden px-2 min-[900px]:block">
            <div className="text-base font-extrabold tracking-wide text-white">Simplicity Dynamics</div>
            <div className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[rgba(215,226,236,.55)]">
              Admin portal
            </div>
          </div>
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`flex-none whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-semibold transition min-[900px]:border-l-[3px] ${
                activeTab === tab
                  ? 'bg-[rgba(158,203,247,.18)] text-[#9ecbf7] min-[900px]:border-[#9ecbf7]'
                  : 'text-[#d7e2ec] hover:bg-[rgba(158,203,247,.15)] min-[900px]:border-transparent'
              }`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>

        <main className="min-w-0 flex-1 p-6">
          {error && <div className="mb-4 rounded-lg bg-[#d96a5b] p-3 text-white">{error}</div>}

          {activeTab === 'Overview' && (
            <OverviewTab
              customers={customers}
              opsOverviewStatus={opsOverviewStatus}
              securitySweep={opsOverview?.securitySweep}
            />
          )}

          {activeTab === 'Customers' && (
            <section className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
              <h2 className="mb-4 text-xl font-bold text-[#1d2b33]">Manage Customers</h2>
              <form onSubmit={handleCustomerSubmit} className="mb-6 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  placeholder="Company name"
                  required
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                />
                <input
                  type="email"
                  placeholder="Owner's email"
                  required
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                />
                <input
                  type="text"
                  placeholder="Owner's name"
                  title="The person who owns this account - shown as their name in the office app"
                  maxLength={80}
                  required
                  value={newCustomer.ownerName}
                  onChange={(e) => setNewCustomer({ ...newCustomer, ownerName: e.target.value })}
                  className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                />
                <button
                  type="submit"
                  disabled={isCreatingCustomer}
                  className="rounded-lg bg-[#2f66b8] px-4 py-2 font-semibold text-white transition hover:bg-[#285aa5] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCreatingCustomer ? 'Adding…' : 'Add Customer'}
                </button>
              </form>
              <CustomerTable
                customers={customers}
                onSendPasswordReset={handleSendPasswordReset}
                onEditCustomer={handleEditCustomer}
                onDeleteCustomer={handleDeleteCustomer}
              />
            </section>
          )}

          {activeTab === 'Support' && (
            <section className="mb-6 rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-[#1d2b33]">Early Access Requests</h2>
                <span className="rounded-full bg-[#dcebfb] px-2 py-0.5 text-xs font-semibold text-[#2f66b8]">
                  {earlyAccessRequests.filter((r) => r.status === 'new').length} new
                </span>
                <button
                  className="ml-auto rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-3 py-1 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa] disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => dispatch(fetchEarlyAccessRequests())}
                  disabled={earlyAccessStatus === 'loading'}
                >
                  Refresh
                </button>
              </div>
              {earlyAccessError && (
                <div className="mb-4 rounded-lg bg-[#d96a5b] p-3 text-white">{earlyAccessError}</div>
              )}
              {earlyAccessStatus === 'loading' && earlyAccessRequests.length === 0 && (
                <p className="text-[#5b6d7a]">Loading requests...</p>
              )}
              {earlyAccessStatus === 'succeeded' && earlyAccessRequests.length === 0 && (
                <p className="text-[#5b6d7a]">No early access requests yet. They arrive from the website form.</p>
              )}
              <div className="space-y-3">
                {earlyAccessRequests.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-4 shadow-[0_1px_3px_rgba(29,43,51,.08)]"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="text-base font-semibold text-[#1d2b33]">{r.companyName || 'Unknown company'}</h3>
                        <p className="text-sm text-[#5b6d7a]">
                          {r.contactName} &lt;
                          <a href={`mailto:${encodeURIComponent(r.email).replace('%40', '@')}`} className="text-[#3d78c9] underline hover:text-[#2f5a9e]">
                            {r.email}
                          </a>
                          &gt;
                          {r.phone ? ` · ${r.phone}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.trade && (
                          <span className="rounded-full bg-[#dcebfb] px-2 py-1 text-xs font-semibold text-[#2f66b8]">{r.trade}</span>
                        )}
                        {r.crewSize && (
                          <span className="rounded-full bg-[rgba(150,165,180,.18)] px-2 py-1 text-xs font-semibold text-[#64798a]">
                            {r.crewSize} people
                          </span>
                        )}
                        <select
                          value={r.status}
                          disabled={savingRequestId === r.id}
                          onChange={(e) => {
                            setSavingRequestId(r.id);
                            dispatch(setEarlyAccessStatus({ requestId: r.id, status: e.target.value })).finally(() =>
                              setSavingRequestId((cur) => (cur === r.id ? null : cur))
                            );
                          }}
                          className={`rounded-full border px-2 py-1 text-xs font-semibold ${
                            r.status === 'new'
                              ? 'border-[rgba(224,162,60,.5)] bg-[rgba(224,162,60,.15)] text-[#9a6b1a]'
                              : r.status === 'contacted'
                                ? 'border-[rgba(46,161,131,.5)] bg-[rgba(46,161,131,.12)] text-[#1f7a62]'
                                : 'border-[rgba(150,165,180,.4)] bg-[rgba(150,165,180,.18)] text-[#64798a]'
                          }`}
                        >
                          <option value="new">new</option>
                          <option value="contacted">contacted</option>
                          <option value="closed">closed</option>
                        </select>
                      </div>
                    </div>
                    {r.message && <p className="mt-3 whitespace-pre-wrap text-sm text-[#1d2b33]">{r.message}</p>}
                    <p className="mt-3 text-xs text-[#5b6d7a]">
                      {r.lastSubmittedAt ? new Date(r.lastSubmittedAt).toLocaleString() : ''}
                      {r.submittedCount > 1 ? ` · submitted ${r.submittedCount} times` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'Support' && (
            <section className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
              <h2 className="mb-4 text-xl font-bold text-[#1d2b33]">Support Tickets</h2>

              <div className="mb-6 flex flex-wrap items-center gap-2">
                {['All', 'Open', 'Closed'].map((filter) => (
                  <button
                    key={filter}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      ticketFilter === filter
                        ? 'bg-[#2f66b8] text-white'
                        : 'border border-[rgba(150,165,180,.4)] bg-white text-[#5b6d7a] hover:bg-[#f4f7fa]'
                    }`}
                    onClick={() => setTicketFilter(filter)}
                  >
                    {filter}
                  </button>
                ))}
                <button
                  className="ml-auto rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-3 py-1 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa] disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => dispatch(fetchSupportTickets())}
                  disabled={supportStatus === 'loading'}
                >
                  Refresh
                </button>
              </div>

              {supportError && (
                <div className="mb-4 rounded-lg bg-[#d96a5b] p-3 text-white">{supportError}</div>
              )}

              {supportStatus === 'loading' && (
                <p className="text-[#5b6d7a]">Loading support tickets...</p>
              )}

              {supportStatus === 'succeeded' && filteredTickets.length === 0 && (
                <p className="text-[#5b6d7a]">No support tickets yet.</p>
              )}

              <div className="space-y-4">
                {filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-4 shadow-[0_1px_3px_rgba(29,43,51,.08)]"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="text-base font-semibold text-[#1d2b33]">
                          {ticket.companyName || 'Unknown company'}
                        </h3>
                        <p className="text-sm text-[#5b6d7a]">
                          {ticket.requesterName} &lt;{ticket.requesterEmail}&gt;
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="rounded-full bg-[#dcebfb] px-2 py-1 text-xs font-semibold text-[#2f66b8]">
                          {ticket.topic}
                        </span>
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${
                            ticket.status === 'open'
                              ? 'bg-[rgba(224,162,60,.15)] text-[#9a6b1a]'
                              : 'bg-[rgba(150,165,180,.18)] text-[#64798a]'
                          }`}
                        >
                          {ticket.status}
                        </span>
                      </div>
                    </div>

                    <p className="mt-3 whitespace-pre-wrap text-sm text-[#1d2b33]">{ticket.message}</p>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-[#5b6d7a]">
                        {ticket.platform ? `${ticket.platform} · ` : ''}
                        {ticket.appVersion ? `v${ticket.appVersion} · ` : ''}
                        {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : ''}
                      </p>
                      <div className="flex items-center space-x-3">
                        {ticket.screenshotUrl && (
                          <a
                            href={ticket.screenshotUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#3d78c9] underline hover:text-[#2f5a9e]"
                            title="Link expires in ~15 minutes"
                          >
                            View screenshot
                          </a>
                        )}
                        <button
                          className="rounded-lg bg-[#2f66b8] px-3 py-1 text-sm font-semibold text-white transition hover:bg-[#285aa5]"
                          onClick={() => handleToggleTicketStatus(ticket)}
                        >
                          {ticket.status === 'open' ? 'Close Ticket' : 'Reopen Ticket'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'Settings' && <SettingsTab />}
        </main>
      </div>

      <footer className="border-t border-[rgba(150,165,180,.25)] bg-white py-4 text-center text-sm text-[#5b6d7a]">
        <p>&copy; 2026 Simplicity Dynamics</p>
      </footer>
    </div>
  );
};

export default AdminDashboard;
